import { useMemo, useCallback } from "react";

// Components
import useTranslate from "hooks/useTranslate";

// Selectors
import { useSelector } from "react-redux";
import * as userSelectors from "redux/user/selectors";

// Hooks
import { getLocalStorageItem } from "utils/localStorage";
import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import useFormatBonusBalance from "hooks/useFormatBonusBalance";
import { useLocalizationStore } from "stores/localization";
import { BonusItem, BonusListType } from "../../../types/Bonus";
import useFormatNumber from "../../../hooks/useFormatNumber";
import WelcomeBonusCardItem from "../../molecules/WelcomeBonusCardItem";

import "./styles.sass";

function extractNumber(str) {
  if (typeof str !== "string") {
    return 0;
  }
  return parseFloat(str?.replace(/[^\d.-]/g, ""));
}

type DescriptionNominalProps = {
  bonus: BonusListType;
};

const DescriptionNominal = (props: DescriptionNominalProps) => {
  const { bonus } = props;
  const t = useTranslate();
  const language = useLocalizationStore((state) => state.language);
  const currency = useSelector(userSelectors.currency);
  const selectedLanguage =
    language || getLocalStorageItem(STORAGE_LANGUAGE_NAME);
  const formatNumber = useFormatNumber();
  const formatBalance = useFormatBonusBalance();
  const bonusMinAmount =
    "minAmount" in bonus &&
    !!bonus.minAmount &&
    formatNumber(bonus.minAmount, 0, true);

  const totalWager = useMemo(() => {
    if ("amount" in bonus) {
      return (bonus.amount / 100) * bonus.wager;
    }
    return 0;
  }, [bonus]);

  const playedAmount = useMemo(() => {
    if ("amount" in bonus && "wagerAmountRemain" in bonus) {
      return totalWager - bonus.wagerAmountRemain / 100;
    }
    return 0;
  }, [bonus, totalWager]);

  const firstItemProgress = useMemo(() => {
    if (
      selectedLanguage.toUpperCase() === "AZ" ||
      selectedLanguage.toUpperCase() === "TR" ||
      selectedLanguage.toUpperCase() === "BN" ||
      selectedLanguage.toUpperCase() === "UR" ||
      selectedLanguage.toUpperCase() === "MM" ||
      selectedLanguage.toUpperCase() === "HI"
    ) {
      return formatNumber(totalWager, 0, true);
    }
    return formatNumber(playedAmount, 0, true);
  }, [selectedLanguage, totalWager, playedAmount, formatNumber]);

  const lastItemProgress = useMemo(() => {
    if (
      selectedLanguage.toUpperCase() === "AZ" ||
      selectedLanguage.toUpperCase() === "TR" ||
      selectedLanguage.toUpperCase() === "BN" ||
      selectedLanguage.toUpperCase() === "UR" ||
      selectedLanguage.toUpperCase() === "MM" ||
      selectedLanguage.toUpperCase() === "HI"
    ) {
      return formatNumber(playedAmount, 0, true);
    }
    return formatNumber(totalWager, 0, true);
  }, [selectedLanguage, totalWager, playedAmount, formatNumber]);

  // Wrap addCategorySpinsIfMissing with useCallback to avoid re-creation on every render
  const addCategorySpinsIfMissing = useCallback(
    (currentBonus: BonusListType): BonusItem[] => {
      const bonusList = [
        {
          id: 14,
          name: "bonus.back.wager",
          value: `x${currentBonus.wager}`,
        },
        {
          id: 15,
          name: "bonus.title.necessary.conditions",
          value:
            `${currency?.sign} ${formatBalance(extractNumber(bonusMinAmount))}` ||
            "",
        },
        {
          id: 16,
          name: "bonus.title.played.progress",
          value: `${firstItemProgress} ${t("bonus.title.remain.of")} ${lastItemProgress}`,
        },
      ];

      // Check if 'bonus.back.category.spins' exists, if not, add it
      const hasCategory = bonusList.some(
        (item) => item.name === "bonus.back.category.spins",
      );

      if (!hasCategory) {
        bonusList.unshift({
          id: 11,
          name: "bonus.back.category.spins",
          value: "casino.sidebar.slots",
        });
      }

      return bonusList;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bonusMinAmount, firstItemProgress, lastItemProgress, t],
  );

  // Using the helper function to create the bonus deposit list
  const BONUS_DEPOSIT_LIST_ITEM = useMemo(
    () => addCategorySpinsIfMissing(bonus),
    [bonus, addCategorySpinsIfMissing],
  );

  return (
    <div className="DescriptionNominal">
      <div className="DescriptionNominal__list">
        {BONUS_DEPOSIT_LIST_ITEM.map((item) => (
          <WelcomeBonusCardItem key={item.id} bonus={item} />
        ))}
      </div>
    </div>
  );
};

export default DescriptionNominal;
